import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, adminMe, clearToken, getToken } from "../lib/auth";
import { toast } from "sonner";
import {
  Sparkles,
  LogOut,
  Calendar,
  Inbox,
  Lock,
  Unlock,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  MessageSquare,
  RefreshCw,
  Camera,
  X,
  Save,
  Undo2,
} from "lucide-react";
import {
  SATURDAY_SLOTS as TIME_SLOTS,
  getUpcomingWorkDays,
  formatFriendly,
  formatISODate,
} from "../lib/schedule";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("requests");
  const [quotes, setQuotes] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [staged, setStaged] = useState({}); // "YYYY-MM-DD::slot" -> true (booked)
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const saturdays = useMemo(() => getUpcomingWorkDays(14), []);

  const logout = () => {
    clearToken();
    nav("/admin", { replace: true });
  };

  useEffect(() => {
    if (!getToken()) {
      nav("/admin", { replace: true });
      return;
    }
    adminMe().catch(() => {
      clearToken();
      nav("/admin", { replace: true });
    });
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [q, b] = await Promise.all([
        adminApi.quotes(),
        adminApi.blockedSlots(),
      ]);
      setQuotes(q);
      setBlocked(b);
      setStaged(stagedFromBlocked(b));
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        nav("/admin", { replace: true });
        return;
      }
      toast.error("Could not load data");
    } finally {
      setLoading(false);
    }
  };

  const findBlocked = (date, slot) =>
    blocked.find((b) => b.date === date && b.slot === slot);

  const slotKey = (date, slot) => `${date}::${slot}`;
  const stagedIsBooked = (date, slot) => !!staged[slotKey(date, slot)];

  const toggleStaged = (date, slot) => {
    setStaged((prev) => {
      const k = slotKey(date, slot);
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = true;
      return next;
    });
  };

  const pendingChanges = useMemo(() => {
    const additions = [];
    const removals = [];
    const savedKeys = new Set(blocked.map((b) => slotKey(b.date, b.slot)));
    const stagedKeys = new Set(Object.keys(staged));
    for (const k of stagedKeys) {
      if (!savedKeys.has(k)) {
        const [date, slot] = k.split("::");
        additions.push({ date, slot });
      }
    }
    for (const b of blocked) {
      if (!stagedKeys.has(slotKey(b.date, b.slot))) {
        removals.push(b);
      }
    }
    return { additions, removals, total: additions.length + removals.length };
  }, [staged, blocked]);

  const discardChanges = () => setStaged(stagedFromBlocked(blocked));

  const saveChanges = async () => {
    if (!pendingChanges.total) return;
    setSaving(true);
    try {
      const createdList = [];
      for (const add of pendingChanges.additions) {
        const created = await adminApi.createBlocked(add);
        createdList.push(created);
      }
      for (const rem of pendingChanges.removals) {
        await adminApi.deleteBlocked(rem.id);
      }
      // rebuild blocked from current staged (fetch fresh to pick up server IDs)
      const fresh = await adminApi.blockedSlots();
      setBlocked(fresh);
      setStaged(stagedFromBlocked(fresh));
      toast.success("Schedule saved", {
        description: `${pendingChanges.total} change${pendingChanges.total !== 1 ? "s" : ""} applied.`,
      });
    } catch (err) {
      toast.error("Save failed", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      {/* Top bar */}
      <header className="border-b border-black/5 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#1A365D] text-white">
              <Sparkles className="h-4 w-4" />
            </span>
            <span className="font-serif text-xl text-[#111827] hidden sm:inline">
              No Grime,{" "}
              <span className="italic text-[#1A365D]">Window Shine</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={loadAll}
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#1A365D] hover:bg-[#E8F1F2] transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-[#1A365D] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#2B4C7E] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Title */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-6">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A365D] mb-3">
          Grime Master · Dashboard
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-[#111827] leading-tight">
          Requests &amp; schedule.
        </h1>
        <p className="mt-3 text-[#4B5563] max-w-2xl">
          Review incoming quote requests and block off Saturday slots you
          can&apos;t take.
        </p>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-6">
        <div className="inline-flex rounded-full border border-black/10 bg-white p-1">
          <TabButton
            active={tab === "requests"}
            onClick={() => setTab("requests")}
            icon={<Inbox className="h-4 w-4" />}
            label={`Requests${quotes.length ? ` · ${quotes.length}` : ""}`}
            testid="tab-requests"
          />
          <TabButton
            active={tab === "schedule"}
            onClick={() => setTab("schedule")}
            icon={<Calendar className="h-4 w-4" />}
            label="Schedule"
            testid="tab-schedule"
          />
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {loading ? (
          <div className="text-[#4B5563] text-sm">Loading…</div>
        ) : tab === "requests" ? (
          <RequestsList quotes={quotes} onOpenPhoto={(uri) => setLightbox({ uri })} />
        ) : (
          <ScheduleGrid
            saturdays={saturdays}
            stagedIsBooked={stagedIsBooked}
            onToggle={toggleStaged}
            pending={pendingChanges.total}
            saving={saving}
            onSave={saveChanges}
            onDiscard={discardChanges}
          />
        )}
      </main>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox.uri}
            alt="Customer photo"
            className="max-h-[90vh] max-w-full rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs uppercase tracking-[0.15em] transition-colors ${
        active
          ? "bg-[#1A365D] text-white"
          : "text-[#4B5563] hover:text-[#1A365D]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function RequestsList({ quotes, onOpenPhoto }) {
  if (!quotes.length) {
    return (
      <div className="rounded-3xl bg-white border border-black/5 p-12 text-center">
        <Inbox className="h-10 w-10 mx-auto text-[#1A365D]/40" />
        <div className="mt-4 font-serif text-2xl text-[#111827]">
          No requests yet
        </div>
        <p className="text-sm text-[#4B5563] mt-2">
          New quote submissions will appear here — you&apos;ll also get a text.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="requests-list">
      {quotes.map((q) => (
        <QuoteCard key={q.id} q={q} onOpenPhoto={onOpenPhoto} />
      ))}
    </div>
  );
}

function QuoteCard({ q, onOpenPhoto }) {
  const b = q.breakdown || {};
  const hasBreakdown =
    (b.single_story_outside_windows || 0) +
      (b.inside_windows || 0) +
      (b.second_story_outside_windows || 0) >
    0;
  const date = q.created_at ? new Date(q.created_at) : null;
  return (
    <article
      className="rounded-3xl bg-white border border-black/5 p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.03)]"
      data-testid={`quote-card-${q.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-serif text-2xl text-[#111827]">{q.name}</div>
            {q.property_type && (
              <span className="text-[10px] uppercase tracking-[0.2em] bg-[#E8F1F2] text-[#1A365D] px-2 py-1 rounded-full border border-[#1A365D]/15">
                {q.property_type}
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#4B5563]">
            <a
              href={`tel:${q.phone}`}
              className="inline-flex items-center gap-1.5 hover:text-[#1A365D]"
            >
              <Phone className="h-3.5 w-3.5" />
              {q.phone}
            </a>
            {q.email && (
              <a
                href={`mailto:${q.email}`}
                className="inline-flex items-center gap-1.5 hover:text-[#1A365D]"
              >
                <Mail className="h-3.5 w-3.5" />
                {q.email}
              </a>
            )}
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {q.address}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.15em] text-[#4B5563]">
            Received
          </div>
          <div className="font-serif text-xl text-[#1A365D]">
            {date
              ? date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })
              : ""}
          </div>
          <div className="text-xs text-[#4B5563] mt-1">
            {date
              ? date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                })
              : ""}
          </div>
        </div>
      </div>

      {hasBreakdown && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Stat
            label="1st-story out"
            v={`${b.single_story_outside_windows || 0}`}
            sub={`$${Number(b.single_story_outside_total || 0).toFixed(0)}`}
          />
          <Stat
            label="Inside"
            v={`${b.inside_windows || 0}`}
            sub={`$${Number(b.inside_total || 0).toFixed(0)}`}
          />
          <Stat
            label="2nd-story out"
            v={`${b.second_story_outside_windows || 0}`}
            sub={`$${Number(b.second_story_total || 0).toFixed(0)}`}
          />
          <Stat
            label={b.service_town || "San Angelo"}
            v={`${b.round_trip_miles || 0} mi RT`}
            sub={`$${Number(b.mileage_fee || 0).toFixed(0)}`}
          />
        </div>
      )}

      {q.preferred_date_note && (
        <div className="mt-4 text-sm text-[#111827] bg-[#E8F1F2] border border-[#1A365D]/10 rounded-2xl p-3">
          <span className="font-semibold text-[#1A365D] uppercase tracking-[0.15em] text-xs mr-2">
            Preferred:
          </span>
          {q.preferred_date_note}
        </div>
      )}

      {q.notes && (
        <div className="mt-3 flex gap-2 text-sm text-[#4B5563]">
          <MessageSquare className="h-4 w-4 shrink-0 mt-0.5 text-[#1A365D]" />
          <span>{q.notes}</span>
        </div>
      )}

      {Array.isArray(q.photos) && q.photos.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-2">
            <Camera className="h-3.5 w-3.5 text-[#1A365D]" />
            {q.photos.length} photo{q.photos.length !== 1 ? "s" : ""}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {q.photos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onOpenPhoto && onOpenPhoto(p)}
                className="relative aspect-square overflow-hidden rounded-xl border border-black/10 bg-white hover:ring-2 hover:ring-[#1A365D]/40 transition-shadow"
                data-testid={`admin-photo-${q.id}-${i}`}
              >
                <img
                  src={p}
                  alt={`Customer photo ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-black/5 flex items-center justify-between text-xs text-[#4B5563] flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5" />
          {hasBreakdown
            ? `Estimated $${Number(b.estimated_total || 0).toFixed(0)}`
            : "On-site estimate pending"}
        </span>
        <div className="flex items-center gap-4">
          <span
            className={`uppercase tracking-[0.15em] ${
              q.sms_status === "sent"
                ? "text-green-600"
                : q.sms_status === "failed"
                ? "text-amber-600"
                : "text-[#4B5563]"
            }`}
          >
            SMS: {q.sms_status || "—"}
          </span>
          <span
            className={`uppercase tracking-[0.15em] ${
              q.email_status === "sent"
                ? "text-green-600"
                : q.email_status === "failed"
                ? "text-amber-600"
                : "text-[#4B5563]"
            }`}
          >
            Email: {q.email_status || "—"}
          </span>
        </div>
      </div>
    </article>
  );
}

function Stat({ label, v, sub }) {
  return (
    <div className="rounded-2xl bg-[#F9F9F6] p-4">
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#4B5563]">
        {label}
      </div>
      <div className="font-serif text-2xl text-[#111827] leading-none mt-1">
        {v}
      </div>
      <div className="text-xs text-[#4B5563] mt-1">{sub}</div>
    </div>
  );
}

function stagedFromBlocked(list) {
  const out = {};
  for (const b of list || []) out[`${b.date}::${b.slot}`] = true;
  return out;
}

function ScheduleGrid({
  saturdays,
  stagedIsBooked,
  onToggle,
  pending,
  saving,
  onSave,
  onDiscard,
}) {
  return (
    <div
      className="rounded-3xl bg-white border border-black/5 p-6 md:p-8"
      data-testid="schedule-grid"
    >
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-serif text-2xl text-[#111827]">
            Upcoming availability
          </div>
          <p className="text-sm text-[#4B5563] mt-1 max-w-xl">
            Mon&ndash;Sat, 9&nbsp;AM – 3&nbsp;PM. Tap an <em>Open</em> slot to
            mark it <em>Booked</em>. Changes are staged &mdash; click
            <strong> Save</strong> to publish them to the public site.
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-[#4B5563]">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-[#E8F1F2] border border-[#1A365D]/30" />
            Open
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-[#1A365D]" />
            Booked
          </span>
        </div>
      </div>

      {/* Save bar */}
      <div
        className={`sticky top-[72px] z-10 mb-4 flex items-center justify-between gap-3 rounded-2xl px-4 py-3 border transition-colors ${
          pending
            ? "bg-[#1A365D] text-white border-[#1A365D]"
            : "bg-[#F9F9F6] text-[#4B5563] border-black/5"
        }`}
        data-testid="schedule-save-bar"
      >
        <span className="text-xs uppercase tracking-[0.15em]">
          {pending
            ? `${pending} unsaved change${pending !== 1 ? "s" : ""}`
            : "All changes saved"}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onDiscard}
            disabled={!pending || saving}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors disabled:opacity-40 ${
              pending
                ? "border-white/40 text-white hover:bg-white/10"
                : "border-black/10 text-[#4B5563]"
            }`}
            data-testid="schedule-discard"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Discard
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!pending || saving}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#1A365D] px-5 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#E8F1F2] transition-colors disabled:opacity-50"
            data-testid="schedule-save"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#4B5563] border-b border-black/5">
              <th className="py-3 pr-4">Day</th>
              {TIME_SLOTS.map((s) => (
                <th key={s} className="py-3 px-2 text-center">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {saturdays.map((d) => {
              const iso = formatISODate(d);
              return (
                <tr
                  key={iso}
                  className="border-b border-black/5 last:border-b-0"
                >
                  <td className="py-4 pr-4">
                    <div className="font-serif text-lg text-[#111827]">
                      {formatFriendly(d)}
                    </div>
                    <div className="text-xs text-[#4B5563]">{iso}</div>
                  </td>
                  {TIME_SLOTS.map((slot) => {
                    const booked = stagedIsBooked(iso, slot);
                    return (
                      <td key={slot} className="py-3 px-2 text-center">
                        <button
                          onClick={() => onToggle(iso, slot)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs uppercase tracking-[0.12em] border transition-all hover:-translate-y-0.5 ${
                            booked
                              ? "bg-[#1A365D] text-white border-[#1A365D]"
                              : "bg-[#E8F1F2] text-[#1A365D] border-[#1A365D]/20 hover:bg-white"
                          }`}
                          data-testid={`admin-slot-${iso}-${slot.replace(/\s+/g, "")}`}
                        >
                          {booked ? (
                            <>
                              <Lock className="h-3 w-3" />
                              Booked
                            </>
                          ) : (
                            <>
                              <Unlock className="h-3 w-3" />
                              Open
                            </>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
